<template>
  <v-card
    class="d-flex flex-column roody-chat-interface mx-auto"
    :class="$vuetify.breakpoint.smAndDown ? 'rounded-0 elevation-0 fill-height' : 'rounded-xl elevation-20 mt-5'"
    :height="$vuetify.breakpoint.smAndDown ? '100vh' : '85vh'"
    :max-width="$vuetify.breakpoint.mdAndUp ? '900' : '100%'"
    dark>
    <v-app-bar absolute flat color="transparent" height="70" class="glass-header px-2" style="z-index: 10">
      <v-avatar color="orange accent-4" size="40" class="mr-3 elevation-10 glow-avatar">
        <v-icon color="white" small>mdi-robot</v-icon>
      </v-avatar>

      <div class="d-flex flex-column">
        <div class="font-weight-black white--text text-subtitle-1 lh-1 tracking-wide">ROODY</div>
        <div class="caption grey--text text--lighten-1 d-flex align-center"><span class="online-dot mr-1"></span> AI Assistant</div>
      </div>

      <v-spacer></v-spacer>

      <v-menu offset-y content-class="glass-menu rounded-xl">
        <template v-slot:activator="{ on, attrs }">
          <v-btn small rounded text color="white" v-bind="attrs" v-on="on" class="glass-btn pl-2 pr-3">
            <v-icon left small color="orange accent-3">mdi-map-marker</v-icon>
            <span class="text-truncate" style="max-width: 100px">{{ selectedStudioName }}</span>
            <v-icon right x-small>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list dense color="#1e293b" dark class="py-0">
          <v-list-item v-for="s in mappedStudios" :key="s._id" @click="studioId = s._id">
            <v-list-item-title class="caption font-weight-bold">{{ s.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn icon small color="grey lighten-2" @click="clearChat" class="ml-1">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </v-app-bar>

    <div ref="chatArea" class="flex-grow-1 overflow-y-auto chat-background pt-16 pb-16 px-0" style="scroll-behavior: smooth">
      <v-fade-transition>
        <div v-if="messages.length === 0" class="d-flex flex-column align-center justify-center fill-height pb-16">
          <div class="video-bg-placeholder rounded-circle mb-6 d-flex align-center justify-center pulse-ring">
            <v-icon size="40" color="white">mdi-arm-flex</v-icon>
          </div>
          <div class="text-h4 font-weight-black white--text mb-1">Let's Go!</div>
          <div class="text-body-2 grey--text text--lighten-1">Was steht heute an, Dom?</div>
        </div>
      </v-fade-transition>

      <transition-group name="staggered-fade" tag="div" class="px-3 pt-4">
        <div v-for="msg in messages" :key="msg.id" class="d-flex flex-column mb-6 w-100">
          <div v-if="msg.role === 'user'" class="align-self-end user-bubble pa-3 px-4 text-body-2 font-weight-medium mb-1">
            {{ msg.content }}
          </div>

          <div v-else class="w-100">
            <div class="d-flex align-center mb-2 pl-1">
              <span class="caption grey--text font-weight-bold ml-1">Roody</span>
            </div>

            <div v-if="msg.type === 'text'" class="bot-text-bubble pa-3 text-body-2 grey--text text--lighten-2">
              {{ msg.content }}
            </div>

            <div v-else-if="msg.type === 'courses'" class="course-swipe-container">
              <div v-if="!msg.data.length" class="bot-text-bubble pa-3">Keine Kurse gefunden.</div>
              <div v-else class="horizontal-scroll-snap pb-4 pl-1">
                <div
                  v-for="(group, dateKey) in groupedCourses(msg.data)"
                  :key="dateKey"
                  class="course-day-card mr-3 glass-card rounded-xl pa-0 overflow-hidden">
                  <div class="day-header pa-3 d-flex justify-space-between align-center gradient-header">
                    <span class="font-weight-black text-body-1 white--text text-uppercase">{{ dateKey }}</span>
                    <v-chip x-small color="black" class="font-weight-bold white--text">{{ group.length }} Kurse</v-chip>
                  </div>

                  <div class="day-body pa-2 overflow-y-auto" style="max-height: 280px">
                    <div
                      v-for="(c, i) in group"
                      :key="i"
                      class="course-item mb-2 pa-3 rounded-lg d-flex align-center relative"
                      @click="openCourseDetail(c)"
                      v-ripple>
                      <div class="time-box rounded-lg font-weight-bold text-caption d-flex flex-column align-center justify-center mr-3">
                        <span>{{ c.time.split(":")[0] }}</span>
                        <span style="opacity: 0.6">{{ c.time.split(":")[1] }}</span>
                      </div>

                      <div class="flex-grow-1">
                        <div class="font-weight-bold text-body-2 white--text line-clamp-1">{{ c.courseName }}</div>
                        <div class="caption grey--text line-clamp-1">{{ c.instructors }} • {{ c.room || "Studio" }}</div>
                      </div>
                      <v-icon small color="grey darken-2">mdi-chevron-right</v-icon>
                    </div>
                  </div>
                </div>
                <div style="min-width: 10px"></div>
              </div>
            </div>

            <div v-else-if="msg.type === 'studio-info'" class="bot-content-wrapper">
              <v-card class="rounded-xl glass-card overflow-hidden elevation-10" max-width="600">
                <div class="studio-hero pa-4 d-flex flex-column justify-end">
                  <div class="text-h5 font-weight-black white--text mb-1">{{ msg.data.name }}</div>
                  <div class="d-flex align-center grey--text text--lighten-3 caption">
                    <v-icon x-small color="white" class="mr-1">mdi-map-marker</v-icon> {{ msg.data.address }}
                  </div>
                </div>

                <div class="d-flex pa-3" style="gap: 8px">
                  <v-btn
                    flex-grow-1
                    small
                    depressed
                    color="rgba(255,255,255,0.1)"
                    class="rounded-lg flex-grow-1 white--text"
                    :href="`tel:${msg.data.phone}`">
                    <v-icon left x-small>mdi-phone</v-icon> Anruf
                  </v-btn>
                  <v-btn
                    flex-grow-1
                    small
                    depressed
                    color="rgba(255,255,255,0.1)"
                    class="rounded-lg flex-grow-1 white--text"
                    :href="msg.data.mapLink"
                    target="_blank">
                    <v-icon left x-small>mdi-navigation</v-icon> Maps
                  </v-btn>
                </div>

                <v-divider class="grey darken-3"></v-divider>

                <div v-if="msg.data.features && msg.data.features.length" class="px-4 py-3">
                  <div class="caption font-weight-bold grey--text text--darken-1 mb-2 text-uppercase">Highlights</div>
                  <div class="d-flex flex-wrap" style="gap: 8px">
                    <div v-for="(feat, fi) in msg.data.features" :key="fi" class="d-flex align-center feature-pill px-2 py-1 rounded-lg">
                      <v-icon x-small color="green accent-3" class="mr-1">mdi-check-circle-outline</v-icon>
                      <span class="caption font-weight-medium white--text">{{ feat }}</span>
                    </div>
                  </div>
                </div>

                <v-divider class="grey darken-3"></v-divider>

                <div class="px-4 py-3">
                  <div class="d-flex align-center mb-3">
                    <v-icon small color="orange accent-3" class="mr-2">mdi-clock-outline</v-icon>
                    <span class="font-weight-bold white--text text-body-2">Öffnungszeiten</span>
                  </div>

                  <div class="opening-hours-grid">
                    <div v-for="(block, bi) in msg.data.openingHours" :key="bi" class="mb-2">
                      <div class="caption orange--text text--lighten-2 font-weight-bold mb-1">{{ block.title }}</div>
                      <div
                        v-for="(h, hi) in block.hours"
                        :key="hi"
                        class="d-flex justify-space-between caption grey--text text--lighten-2 py-1"
                        style="border-bottom: 1px dashed rgba(255, 255, 255, 0.1)">
                        <span>{{ h.text }}</span>
                        <span class="font-weight-bold white--text">{{ h.from }} - {{ h.to }}</span>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="msg.data.holidays.length"
                    class="mt-3 pa-2 rounded red lighten-1 black--text caption font-weight-bold d-flex align-center">
                    <v-icon x-small color="black" class="mr-2">mdi-calendar-alert</v-icon>
                    Es stehen Feiertage an!
                  </div>
                </div>
              </v-card>
            </div>

            <div v-else-if="msg.type === 'prices-overview'" class="bot-content-wrapper">
              <div class="text-subtitle-1 font-weight-bold orange--text ml-1 mb-2">Wähle dein Ziel</div>
              <v-row dense>
                <v-col cols="12" sm="6" v-for="offer in msg.data" :key="offer.id">
                  <v-card class="rounded-xl pa-0 cursor-pointer price-card-gradient fill-height" @click="triggerPriceDetail(offer.title)" v-ripple>
                    <div class="pa-4 d-flex justify-space-between align-center h-100">
                      <div class="d-flex flex-column justify-center">
                        <div class="white--text font-weight-black text-h6 line-clamp-1">{{ offer.title }}</div>
                        <div class="caption white--text opacity-70 line-clamp-2 mt-1">{{ offer.description }}</div>
                      </div>
                      <v-icon color="white" class="ml-2">mdi-arrow-right-circle</v-icon>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <div v-else-if="msg.type === 'prices-detail'" class="bot-content-wrapper">
              <v-card class="glass-card rounded-xl pa-4" max-width="700">
                <div class="d-flex align-start justify-space-between mb-2">
                  <div>
                    <div class="text-h5 font-weight-black white--text mb-1">{{ msg.data.title }}</div>
                    <div class="text-body-2 grey--text text--lighten-1" style="max-width: 90%">{{ msg.data.description }}</div>
                  </div>
                  <v-avatar color="rgba(255,255,255,0.1)" size="40" rounded>
                    <v-icon color="white">mdi-tag-text-outline</v-icon>
                  </v-avatar>
                </div>

                <v-divider class="my-4 grey darken-3"></v-divider>

                <div class="price-list">
                  <div
                    v-for="(v, idx) in msg.data.variants"
                    :key="idx"
                    class="mb-3 pa-4 rounded-lg price-variant-row relative overflow-hidden"
                    :class="v.isInstallment ? 'installment-bg' : 'standard-bg'">
                    <div v-if="v.promoActive" class="absolute-promo-tag caption font-weight-bold orange accent-4 black--text px-2 py-0 rounded-bl-lg">
                      PROMO
                    </div>

                    <div class="d-flex justify-space-between align-center mb-2">
                      <div class="d-flex align-center">
                        <v-icon v-if="v.isInstallment" small color="blue lighten-2" class="mr-2">mdi-calendar-month</v-icon>
                        <v-icon v-else small color="green accent-3" class="mr-2">mdi-calendar-check</v-icon>
                        <span class="font-weight-bold white--text text-body-1">{{ v.label }}</span>
                      </div>
                      <div class="d-flex flex-column align-end">
                        <span class="green--text text--accent-3 font-weight-black text-h5">{{ v.price }}.-</span>
                        <span v-if="v.isInstallment" class="caption grey--text">monatlich</span>
                      </div>
                    </div>

                    <div v-if="v.addons && v.addons.length" class="mt-3 pt-2" style="border-top: 1px solid rgba(255, 255, 255, 0.1)">
                      <div class="caption font-weight-bold grey--text text--darken-1 mb-2">MÖGLICHE ADD-ONS</div>
                      <div class="addon-list">
                        <div v-for="a in v.addons" :key="a.name" class="d-flex justify-space-between align-center py-1">
                          <span class="text-body-2 grey--text text--lighten-2">{{ a.name }}</span>
                          <span class="text-body-2 font-weight-bold orange--text text--accent-1">+ {{ a.price }}.-</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <v-btn block x-large color="white" class="black--text font-weight-bold mt-4 rounded-lg" :href="msg.data.contractUrl" target="_blank">
                  <v-icon left>mdi-pen</v-icon> Jetzt starten
                </v-btn>
                <v-btn block small text color="grey" class="mt-2" @click="handleAction('get_prices_overview', 'Zurück')"> Zurück zur Auswahl </v-btn>
              </v-card>
            </div>
          </div>
        </div>
      </transition-group>

      <div v-if="loading" class="d-flex justify-center mt-4">
        <div class="typing-bubble px-4 py-2 rounded-xl"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div>
      </div>
    </div>

    <div class="absolute-bottom d-flex justify-center pb-6 w-100 glass-footer pt-4 pointer-events-none">
      <div class="glass-dock px-4 py-2 rounded-xl d-flex align-center pointer-events-auto elevation-10" style="gap: 16px">
        <v-tooltip top v-for="(btn, i) in quickButtons" :key="i" color="#1e293b">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              fab
              small
              elevation="0"
              :color="activeAction === btn.action ? 'orange accent-3' : 'rgba(255,255,255,0.1)'"
              class="dock-btn"
              @click="handleAction(btn.action, btn.label)">
              <v-icon small :color="activeAction === btn.action ? 'black' : 'white'">{{ btn.icon }}</v-icon>
            </v-btn>
          </template>
          <span>{{ btn.label }}</span>
        </v-tooltip>
      </div>
    </div>

    <v-bottom-sheet v-model="showCourseDetail" content-class="glass-sheet" max-width="600">
      <div v-if="selectedCourse" class="white--text pa-5 rounded-t-xl" style="background: #1e293b; border-top: 1px solid rgba(255, 255, 255, 0.1)">
        <div class="d-flex justify-center mb-4">
          <div class="rounded-pill grey lighten-2" style="width: 40px; height: 4px; opacity: 0.3"></div>
        </div>

        <div class="d-flex justify-space-between align-start mb-4">
          <div>
            <div class="orange--text text--accent-3 font-weight-bold text-uppercase caption mb-1">
              {{ selectedCourse.day }}, {{ selectedCourse.time }} Uhr
            </div>
            <div class="text-h4 font-weight-black white--text lh-1 mb-1">{{ selectedCourse.courseName }}</div>
            <div class="caption grey--text">{{ selectedCourse.room || "Kursraum" }}</div>
          </div>
          <v-avatar color="grey darken-3" rounded size="56">
            <v-icon color="white" large>mdi-dumbbell</v-icon>
          </v-avatar>
        </div>

        <div class="d-flex align-center mb-6 pa-3 rounded-lg" style="background: rgba(255, 255, 255, 0.05)">
          <v-avatar size="40" color="orange accent-3" class="mr-3">
            <span class="black--text font-weight-bold text-h6">{{ selectedCourse.instructors.charAt(0) }}</span>
          </v-avatar>
          <div>
            <div class="caption grey--text">Trainer/in</div>
            <div class="font-weight-bold text-body-1">{{ selectedCourse.instructors }}</div>
          </div>
        </div>

        <div class="text-body-1 grey--text text--lighten-2 mb-6" style="line-height: 1.6">
          {{ selectedCourse.description || "Ein intensives Workout für den ganzen Körper. Komm vorbei und power dich aus!" }}
        </div>

        <v-btn block x-large color="orange accent-3" class="black--text font-weight-bold rounded-xl" @click="showCourseDetail = false">
          Alles klar!
        </v-btn>
      </div>
    </v-bottom-sheet>
  </v-card>
</template>

<script>
import api from "@/api"
import { mapState } from "vuex"
import moment from "moment"

export default {
  name: "RoodyChatInterface",
  data() {
    return {
      messages: [],
      loading: false,
      studioId: "5f326a24ae8863640c859e75",
      activeAction: null,
      showCourseDetail: false,
      selectedCourse: null,
      quickButtons: [
        { label: "Studio Info", icon: "mdi-storefront-outline", action: "get_studio_info" },
        { label: "Kurse", icon: "mdi-yoga", action: "get_courses" },
        { label: "Preise", icon: "mdi-ticket-confirmation-outline", action: "get_prices_overview" },
      ],
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    mappedStudios() {
      if (!this.storeStudios) return []
      return this.storeStudios
        .filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
        .map((studio) => ({ _id: studio._id, name: studio.name }))
    },
    selectedStudioName() {
      const s = this.mappedStudios.find((x) => x._id === this.studioId)
      return s ? s.name : "Wähle Studio"
    },
  },
  methods: {
    groupedCourses(courseList) {
      const groups = {}
      const today = moment().startOf("day")
      const tomorrow = moment().add(1, "days").startOf("day")

      courseList.forEach((c) => {
        const cDate = moment.unix(c.ts).startOf("day")
        let label = ""
        if (cDate.isSame(today)) label = "Heute"
        else if (cDate.isSame(tomorrow)) label = "Morgen"
        else label = moment.unix(c.ts).locale("de").format("dddd, DD.MM.")

        if (!groups[label]) groups[label] = []
        groups[label].push(c)
      })
      return groups
    },
    openCourseDetail(course) {
      this.selectedCourse = course
      this.showCourseDetail = true
    },
    clearChat() {
      this.messages = []
      this.activeAction = null
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatArea
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    async handleAction(actionKey, labelText, payload = null) {
      this.activeAction = actionKey
      if (labelText) {
        this.messages.push({
          id: Date.now(),
          role: "user",
          type: "text",
          content: labelText,
        })
      }
      this.scrollToBottom()
      this.loading = true

      try {
        const response = await api.ai.fake({
          action: actionKey,
          studioId: this.studioId,
          payload: payload,
        })
        if (response) {
          this.messages.push({
            id: Date.now() + 1,
            role: "bot",
            type: response.type,
            data: response.data,
          })
        }
      } catch (e) {
        this.messages.push({
          id: Date.now() + 1,
          role: "bot",
          type: "text",
          content: "Sorry, ich konnte die Daten nicht laden.",
        })
      } finally {
        this.loading = false
        this.scrollToBottom()
      }
    },
    triggerPriceDetail(offerTitle) {
      this.handleAction("get_price_detail", null, offerTitle)
    },
  },
}
</script>

<style scoped lang="scss">
/* --- CORE LAYOUT --- */
.roody-chat-interface {
  background-color: #0f172a;
  font-family: "Inter", sans-serif;
  position: relative;
  background: radial-gradient(circle at top left, #1e293b 0%, #0f172a 40%, #000000 100%);
}

/* --- GLASS UTILS --- */
.glass-header {
  backdrop-filter: blur(12px);
  background: linear-gradient(to bottom, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0) 100%);
}
.glass-footer {
  background: linear-gradient(to top, #0f172a 20%, transparent 100%);
}
.glass-dock {
  background: rgba(30, 41, 59, 0.75);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}
.glass-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.glass-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* --- BUBBLES --- */
.user-bubble {
  background: linear-gradient(135deg, #ff6d00 0%, #ff9100 100%);
  color: white;
  border-radius: 20px 20px 4px 20px;
  max-width: 80%;
  box-shadow: 0 4px 15px rgba(255, 109, 0, 0.3);
}
.bot-text-bubble {
  background: #1e293b;
  border-radius: 4px 20px 20px 20px;
  display: inline-block;
  max-width: 85%;
}
.bot-content-wrapper {
  max-width: 100%;
}

/* --- HORIZONTAL SCROLL --- */
.horizontal-scroll-snap {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 5px;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.course-day-card {
  min-width: 260px;
  max-width: 260px;
  scroll-snap-align: start;
  background: #1e293b;
}
.gradient-header {
  background: linear-gradient(90deg, #ff6d00 0%, #f57c00 100%);
}
.course-item {
  background: rgba(255, 255, 255, 0.03);
  transition: all 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
  &:active {
    transform: scale(0.98);
  }
}
.time-box {
  background: rgba(0, 0, 0, 0.3);
  width: 45px;
  height: 45px;
  color: #fb8c00;
}

/* --- STUDIO INFO --- */
.studio-hero {
  height: 140px;
  background-image: url("https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80");
  background-size: cover;
  background-position: center;
  position: relative;
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, #1e293b 10%, rgba(30, 41, 59, 0.4) 100%);
  }
  > * {
    position: relative;
    z-index: 1;
  }
}
.feature-pill {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* --- PRICES --- */
.price-card-gradient {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s;
  &:hover {
    border-color: #ff9100;
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }
}
.standard-bg {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.installment-bg {
  background: rgba(33, 150, 243, 0.08);
  border: 1px solid rgba(33, 150, 243, 0.2);
}
.absolute-promo-tag {
  position: absolute;
  top: 0;
  right: 0;
}

/* --- ANIMATIONS --- */
.pulse-ring {
  box-shadow: 0 0 0 0 rgba(255, 145, 0, 0.7);
  animation: pulse-orange 2s infinite cubic-bezier(0.66, 0, 0, 1);
}
@keyframes pulse-orange {
  to {
    box-shadow: 0 0 0 20px rgba(255, 145, 0, 0);
  }
}
.video-bg-placeholder {
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, #ff6d00, #ff3d00);
}
.glow-avatar {
  box-shadow: 0 0 15px rgba(255, 109, 0, 0.4);
}

/* --- TYPING INDICATOR --- */
.typing-bubble {
  background: rgba(255, 255, 255, 0.1);
  display: inline-flex;
  align-items: center;
}
.dot {
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
  margin: 0 2px;
  animation: bounce 1.4s infinite ease-in-out both;
}
.dot:nth-child(1) {
  animation-delay: -0.32s;
}
.dot:nth-child(2) {
  animation-delay: -0.16s;
}
@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

/* --- UTILS --- */
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.pointer-events-none {
  pointer-events: none;
}
.pointer-events-auto {
  pointer-events: auto;
}
</style>

<style>
/* GLOBAL OVERRIDES */
.glass-menu .v-list {
  background: #1e293b !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.glass-sheet {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
